import { urlValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import { normalizeNullableStringFields } from "config/resourceUtils";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import resource from "./resource";
import { getUrlDTO, isEnabledField } from "./sharedFields";

const resourceName = "bundle_repository";

const config: ResourceConfig = {
    label: "Bundle repository",
    icon: IconCollection.database,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [
                    logsTab.content,
                    commentsTab.content,
                    referencesUsedByTab.content,
                    metadataTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Remote URL",
            key: "remote_url",
            shown: true,
            sortable: true,
            dto: (value: any) => {
                const content = getUrlDTO(value.remote_url);
                return buildSimpleColumn(
                    content,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "remote_url",
                                  value: value.remote_url,
                              })
                        : undefined
                );
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            is_enabled
            remote_url
            has_api_key
            has_write_key
            api_key_marker
            write_key_marker
            verify_ssl
            server_ca
            check_hostname
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            remote_url
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $is_enabled: Boolean,
            $remote_url: String,
            $api_key: String,
            $write_key: String,
            $verify_ssl: Boolean,
            $server_ca: String,
            $check_hostname: Boolean,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            is_enabled: $is_enabled,
            remote_url: $remote_url,
            api_key: $api_key,
            write_key: $write_key,
            verify_ssl: $verify_ssl,
            server_ca: $server_ca,
            check_hostname: $check_hostname,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $is_enabled: Boolean,
            $remote_url: String,
            $api_key: String,
            $write_key: String,
            $verify_ssl: Boolean,
            $server_ca: String,
            $check_hostname: Boolean,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            is_enabled: $is_enabled,
            remote_url: $remote_url,
            api_key: $api_key,
            write_key: $write_key,
            verify_ssl: $verify_ssl,
            server_ca: $server_ca,
            check_hostname: $check_hostname,
        `,
    },
    form: [
        isEnabledField,
        {
            name: "remote_url",
            component: "url",
            fieldProps: {
                label: "Remote URL",
                docs: {
                    text: "The URL of the bundle repository.",
                },
            },
            validation: urlValidator,
        },
        {
            name: "api_key",
            component: "writeOnlyInput",
            fieldProps: {
                label: "API key",
                clearable: false,
                docs: {
                    text: "The API key to authenticate against the bundle repository. Enter the key itself, or a secret reference such as vault.secret(<vault config>:<path>.<key>) or system.secret(<secret name>).",
                },
            },
            fieldAttr: {
                autoComplete: "new-password",
                placeholder: "Set new API key",
            },
        },
        {
            name: "write_key",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Write key",
                clearable: false,
                docs: {
                    text: "The API key to authenticate write operations against the bundle repository. Enter the key itself, or a secret reference such as vault.secret(<vault config>:<path>.<key>) or system.secret(<secret name>).",
                },
            },
            fieldAttr: {
                autoComplete: "new-password",
                placeholder: "Set new write key",
            },
        },
        {
            name: "verify_ssl",
            component: "switch",
            fieldProps: {
                label: "Verify SSL certificates",
            },
        },
        {
            name: "server_ca",
            component: "textarea",
            fieldProps: {
                label: "Server CA",
            },
        },
        {
            name: "check_hostname",
            component: "switch",
            fieldProps: {
                label: "Check hostname",
            },
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) =>
            normalizeNullableStringFields(resourceData, ["server_ca"]),
    },
};
export default config;
